// GB #677: the auth cookie lives 24 hours but the Firebase ID token inside it
// expires after 1. Any return to a protected route between hour 1 and hour 24
// presents a cookie the middleware cannot verify, so the user is bounced to
// /login even though their session is perfectly recoverable — the browser still
// holds a long-lived Firebase refresh token.
//
// Measured on 2026-07-21: 43 `verify-failed` bounces in a ~12 hour window.
//
// Rather than showing those users a login page, the middleware sends them to a
// silent recovery route that mints a fresh token, rewrites the cookie, and
// forwards them to where they were going. This module is the pure decision core
// of that route, extracted so every branch is testable without mounting the
// component or mocking Firebase (same pattern as resolveAuthLandingAction).
//
// ⚠ REVOCATION WINDOW — DO NOT WIDEN. #1435 accepted that a banned or disabled
// user's already-minted token keeps passing the middleware for up to 1 hour,
// because the verifier does not check revocation. Nothing here stretches that:
// minting a replacement token goes through Google, which REFUSES to issue one
// for a disabled account or a revoked refresh token. A revoked user therefore
// cannot recover here — they fall through to /login — and the token they
// already hold still dies on its original 1 hour schedule.

/** Short-lived cookie that stops a failed recovery from looping. */
export const RECOVERY_GUARD_COOKIE = 'gf_auth_recover';
export const RECOVERY_GUARD_TTL_SECONDS = 30;

/** Where the recovery route lives. */
export const RECOVERY_PATH = '/auth/recover';

/**
 * How long the page waits for Firebase to resolve the signed-in user before
 * giving up and handing off to the login form. Firebase resolves from
 * IndexedDB, normally in tens of milliseconds; this only has to cover a stalled
 * mobile browser, and a user staring at a spinner needs an exit.
 */
export const RECOVERY_TIMEOUT_MS = 8_000;

/**
 * Bound on the token refresh ITSELF.
 *
 * All three review gates independently found the same hole: the budget above
 * only covers Firebase resolving the user, which is a fast local IndexedDB
 * read. The genuinely slow, network-dependent step — `getIdToken(true)` to
 * Google — had no client-side bound at all, and the SDK's own default is 30s
 * (60s on some paths). QA proved it empirically: with the refresh stalled,
 * advancing timers to 80 seconds produced zero navigations.
 *
 * That reintroduces the exact #1435 D1 indefinite-spinner failure on the
 * stalled-mobile population this feature targets. The refresh is now raced
 * against this timer and loses to the login form.
 *
 * Deliberately shorter than RECOVERY_GUARD_TTL_SECONDS so a recovery attempt
 * can never outlive the loop-breaker that guards it (zero-context review).
 */
export const REFRESH_TIMEOUT_MS = 8_000;

export type RecoveryAuthState = 'pending' | 'authed' | 'guest';

export type RecoveryAction =
  /** Firebase has not resolved yet — hold the spinner. */
  | 'wait'
  /** Signed in: mint a fresh token, rewrite the cookie, go to the destination. */
  | 'refresh-and-forward'
  /** No recoverable session (or we waited too long) — hand off to the login form. */
  | 'to-login';

export interface RecoveryInput {
  authState: RecoveryAuthState;
  /** A recovery attempt already started — never start a second one. */
  attempted: boolean;
  /** The wait budget above has elapsed. */
  timedOut: boolean;
}

export function resolveRecoveryAction(input: RecoveryInput): RecoveryAction {
  // Exactly one attempt per mount. Without this a re-render during the async
  // token refresh could kick off a second refresh and a second navigation.
  if (input.attempted) return 'wait';

  // The timeout wins over 'pending'. A user who has been watching a spinner
  // for the full budget gets a usable login form rather than an indefinite
  // wait — this is the failure mode #1435 D1 produced and #677 must not
  // reintroduce.
  if (input.timedOut) return 'to-login';

  switch (input.authState) {
    case 'pending':
      return 'wait';
    case 'guest':
      // Firebase resolved and there is no session: the cookie was stale AND
      // unrecoverable (signed out elsewhere, or storage cleared/evicted). The
      // login form is the correct destination, and it is the honest one.
      return 'to-login';
    case 'authed':
      return 'refresh-and-forward';
    default: {
      // Compile-time exhaustiveness guard.
      const _exhaustive: never = input.authState;
      return _exhaustive;
    }
  }
}

/**
 * Middleware-side decision: a stale-cookie bounce goes to the recovery route
 * only on the FIRST attempt. The guard cookie is what makes this safe — if
 * recovery runs and the destination still fails to verify, the second bounce
 * finds the guard set and goes to /login instead of ping-ponging.
 *
 * Mirrors the `gf_edge_fwd` guard shipped for the #1463 edge forward.
 */
/**
 * Statuses that mean "the token presented is no good" — the recoverable case.
 * Anything else (5xx verifier outage, 408 timeout handled upstream) is not.
 */
export const TOKEN_REJECTED_STATUSES = [401, 403];

export function shouldAttemptRecovery(input: {
  /** Status the verifier returned. ONLY 401 means "this token is no good". */
  status: number;
  hasAuthCookie: boolean;
  hasRecoveryGuard: boolean;
  /** True when the browser is speculatively fetching, not navigating. */
  isPrefetch: boolean;
}): boolean {
  // Recover only on a TOKEN-REJECTION status, never on a verifier outage.
  //
  // ⚠ MEASURED IN PRODUCTION, DO NOT "HARDEN" THIS BACK TO 401-ONLY.
  // `users-verifyUserToken` answers **403** for an expired or invalid token,
  // and `/api/auth` passes that status straight through. Verified live on
  // gearfocus.com 2026-07-25: a request carrying an unverifiable auth cookie
  // gets `{"status":403,"error":"Unauthorized"}`. GB #677's own issue body
  // says the same thing twice — "deterministic 403 bounce" and "a 403 on an
  // iPhone opening /dashboard/purchases".
  //
  // A previous revision gated on 401 only, on the reasoning that 403 means
  // "verified but forbidden, which a new token cannot fix". That reasoning is
  // sound in general and WRONG for this verifier, and it shipped a feature
  // that could never fire even once. Gate on what the call site actually
  // returns, not on what the status code conventionally means.
  //
  // 5xx is still excluded deliberately: that means the VERIFIER is down, and
  // recovery cannot fix a down verifier. Attempting it during a
  // users-verifyUserToken incident would turn one redirect into three page
  // loads plus a Google token exchange for every authenticated user, at the
  // moment the backend is already struggling (adversarial review — that half
  // of the finding was correct and is preserved).
  if (!TOKEN_REJECTED_STATUSES.includes(input.status)) return false;
  if (!input.hasAuthCookie) return false;
  if (input.hasRecoveryGuard) return false;
  // A <Link> prefetch runs middleware and HONOURS Set-Cookie. Without this,
  // hovering the account menu spends the user's one-shot recovery budget on a
  // speculative fetch, and their real click a second later finds the guard set
  // and lands on the login page — defeating the feature precisely in the most
  // common in-app navigation (adversarial review, traced through Next 14's
  // router and edge adapter). Prefetches fall through to the login redirect
  // WITHOUT arming the guard.
  if (input.isPrefetch) return false;
  return true;
}

/**
 * Detects a speculative prefetch. Next sets `x-middleware-prefetch`; browsers
 * send `purpose: prefetch` (and `sec-purpose` for the newer spec).
 */
export function isPrefetchRequest(headers: {
  get(name: string): string | null;
}): boolean {
  if (headers.get('x-middleware-prefetch')) return true;
  const purpose = headers.get('purpose') ?? headers.get('x-purpose');
  if (purpose && purpose.toLowerCase().includes('prefetch')) return true;
  const secPurpose = headers.get('sec-purpose');
  if (secPurpose && secPurpose.toLowerCase().includes('prefetch')) return true;
  return false;
}

/**
 * Builds the recovery URL, preserving where the user was actually going.
 * `safeDest` must already have passed `isSafeRedirect` — this only encodes.
 */
export function buildRecoveryUrl(safeDest: string): string {
  return `${RECOVERY_PATH}?redirect=${encodeURIComponent(safeDest)}`;
}
